import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { AlertTriangle, ArrowLeft, Download, FileText, Lock, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtIDR } from "@/lib/constants";
import InvoiceViewer from "@/components/InvoiceViewer";
import ArchiveDeleteDrawer from "@/components/ArchiveDeleteDrawer";
import { Button } from "@/components/ui/button";

const Section = ({ title, count, children, testid }) => (
  <div className="rounded-md border bg-card p-3" data-testid={testid}>
    <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-stone-500">
      {title}
      {count != null && ` (${count})`}
    </p>
    {children}
  </div>
);

export default function ArchiveFolder() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [delOpen, setDelOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/batches/${batchId}/archive`);
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/");
    }
  }, [batchId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) {
    return (
      <div className="grid place-items-center py-24" data-testid="archive-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const { batch, summary, unavailable } = data;

  return (
    <div data-testid="archive-folder-page">
      <button
        onClick={() => navigate("/archive")}
        className="mb-3 flex h-10 items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> PO Archive
      </button>

      <div className="mb-4 rounded-md border bg-card p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="truncate font-heading text-2xl font-extrabold tracking-tight" data-testid="archive-batch-name">{batch.name}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {batch.region || "—"}
              {batch.closed_at ? ` · Closed ${format(new Date(batch.closed_at), "d MMM yyyy")}` : ""}
              {batch.closed_by_name ? ` by ${batch.closed_by_name}` : ""}
            </p>
          </div>
          <span className="flex shrink-0 items-center gap-1 rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[10px] font-bold text-stone-600">
            <Lock className="h-3 w-3" /> READ-ONLY
          </span>
        </div>
        {batch.recovered && (
          <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-800" data-testid="archive-recovered-note">
            Recovered archive — the original PO Batch record was permanently deleted during testing. Only the surviving
            customer orders, invoices, payments and credits are shown here, grouped under their original batch identity.
            Nothing has been relinked or recreated.
          </p>
        )}
        {unavailable?.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid="archive-unavailable">
            {unavailable.map((u) => (
              <li key={u} className="flex items-start gap-1.5 text-[11px] text-stone-500">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" /> {u}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-3">
        <Section title="Batch Summary" testid="archive-summary">
          <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
            {[
              ["Products", summary.product_count],
              ["Purchases", summary.purchase_count],
              ["Receiving records", summary.receipt_count],
              ["Customer orders", summary.order_count],
              ["Final invoices", summary.invoice_count],
              ["Payments", summary.payment_count],
            ].map(([k, v]) => (
              <div key={k} className="rounded-md border bg-[#EDE4DE] px-2.5 py-2">
                <p className="text-[10px] uppercase tracking-wide text-stone-500">{k}</p>
                <p className="font-heading text-base font-extrabold">{v}</p>
              </div>
            ))}
          </div>
          {user.role !== "viewer" && (
            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-semibold">
              <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5">Invoiced {fmtIDR(summary.invoiced_total)}</span>
              <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">Received {fmtIDR(summary.paid_total)}</span>
              <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700">Outstanding {fmtIDR(summary.outstanding_total)}</span>
            </div>
          )}
        </Section>

        <Section title="Products" count={data.products.length} testid="archive-products">
          {data.products.length === 0 ? (
            <p className="text-xs text-muted-foreground">No product records available.</p>
          ) : (
            <div className="space-y-1.5">
              {data.products.map((p) => (
                <div key={p.id} className="flex items-center gap-2 rounded-md border px-2.5 py-2">
                  {productImage(p) && <img src={productImage(p)} alt="" className="h-8 w-8 shrink-0 rounded-md border object-cover" />}
                  <span className="min-w-0 flex-1 truncate text-xs font-semibold">{p.name}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">Qty {p.ordered_qty} · {p.buying_location || "UNASSIGNED"}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Shopping List / Purchasing History" count={data.purchases.length} testid="archive-purchases">
          {data.purchases.length === 0 ? (
            <p className="text-xs text-muted-foreground">No purchasing records available.</p>
          ) : (
            <div className="space-y-1.5">
              {data.purchases.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-xs">
                  <span className="min-w-0 truncate font-semibold">{p.product_name}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    ×{p.quantity} · {p.location || "—"} · {p.created_at ? format(new Date(p.created_at), "d MMM") : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Customer Orders" count={data.orders.length} testid="archive-orders">
          {data.orders.length === 0 ? (
            <p className="text-xs text-muted-foreground">No customer orders.</p>
          ) : (
            <div className="space-y-1.5">
              {data.orders.map((o) => (
                <button
                  key={o.id}
                  data-testid={`archive-order-${o.id}`}
                  onClick={() => navigate(`/orders/${o.id}`)}
                  className="flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-xs active:scale-[0.99]"
                >
                  <span className="min-w-0 truncate font-semibold">{o.customer_name}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">
                    {o.active_qty} units · {o.status}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Section>

        {user.role !== "viewer" && (
          <Section title="Final Invoices — latest per order" count={data.invoices.length} testid="archive-invoices">
            {data.invoices.length === 0 ? (
              <p className="text-xs text-muted-foreground">No invoices generated.</p>
            ) : (
              <div className="space-y-1.5">
                {data.invoices.map((iv) => (
                  <button
                    key={iv.id}
                    data-testid={`archive-invoice-${iv.id}`}
                    onClick={() => setViewInvoice(iv)}
                    className="flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left active:scale-[0.99]"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-bold">{iv.customer_name}</span>
                      <span className="block text-[11px] text-muted-foreground">
                        <FileText className="mr-1 inline h-3 w-3" />
                        {iv.invoice_number || "—"} · V{iv.version} · LATEST
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-xs font-bold">{fmtIDR(iv.total)}</span>
                      <span className={`block text-[11px] font-semibold ${iv.balance_due > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                        {iv.balance_due > 0 ? `Due ${fmtIDR(iv.balance_due)}` : "PAID"}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Section>
        )}

        {data.receiving.length > 0 && (
          <Section title="Receiving History" count={data.receiving.length} testid="archive-receiving">
            <div className="space-y-1.5">
              {data.receiving.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-xs">
                  <span className="min-w-0 truncate font-semibold">{r.product_name}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">×{r.quantity} · {r.status}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>

      {user.role === "owner" && !batch.recovered && (
        <Button
          variant="outline"
          data-testid="archive-permanent-delete-button"
          className="mt-5 h-12 w-full border-destructive text-destructive hover:bg-red-50"
          onClick={() => setDelOpen(true)}
        >
          <ShieldAlert className="h-4 w-4" /> PERMANENTLY DELETE ARCHIVE
        </Button>
      )}
      {user.role === "owner" && batch.recovered && (
        <p className="mt-5 flex items-start gap-2 rounded-md border border-dashed px-3 py-3 text-[11px] text-muted-foreground" data-testid="archive-recovered-delete-blocked">
          <Download className="mt-0.5 h-3 w-3 shrink-0" />
          Permanent deletion is unavailable for a recovered archive — the original batch record no longer exists, so a
          complete, verifiable backup of the whole batch cannot be guaranteed.
        </p>
      )}

      <ArchiveDeleteDrawer
        batchId={batchId}
        batchName={batch.name}
        open={delOpen}
        onOpenChange={setDelOpen}
        onDeleted={() => navigate("/archive")}
      />
      <InvoiceViewer invoice={viewInvoice} open={!!viewInvoice} onOpenChange={(o) => !o && setViewInvoice(null)} />
    </div>
  );
}
